import { useMutation, useQuery } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import Upload from "@mui/icons-material/Upload";
import {
  Alert,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import {
  getWastePipelineConfig,
  getWastePipelinesForVendor,
  resolveWastePipelineForVendor,
  WastePipelineConfig,
  WastePipelineFileSlot,
} from "encamp-shared/src/pipelines";
import { gql } from "generated-graphql";
import { WastePipelineVendorsQuery } from "generated-graphql/graphql";
import { useCallback, useState } from "react";
import { uploadFileDirectly } from "util/uploadFile";

import { STAFF_PIPELINES_TABLE_QUERY, WASTE_PIPELINE_VENDORS_QUERY } from "./api";
import { DispatchOrganizationPicker, TenantOption } from "./DispatchOrganizationPicker";

export const DISPATCH_WASTE_PIPELINE_RUN_MUTATION = gql(`
  mutation DispatchWastePipelineRun($input: DispatchWastePipelineRunInput!) {
    dispatchWastePipelineRun(input: $input) {
      id
      status
      task
    }
  }
`);

type VendorInfo = WastePipelineVendorsQuery["wastePipelineVendors"][number];

function FileSlotRow({
  slot,
  selectedFile,
  onSelect,
  onClear,
}: {
  slot: WastePipelineFileSlot;
  selectedFile: File | null;
  onSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Button
        variant="outlined"
        component="label"
        startIcon={<Upload />}
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
      >
        {slot.label}
        {slot.required ? " *" : ""}
        <input
          type="file"
          accept={slot.accept}
          onChange={onSelect}
          hidden
          data-testid={`file-input-${slot.key}`}
        />
      </Button>
      {selectedFile && (
        <>
          {/* Vendor export names run long; the name truncates so the button keeps its width. */}
          <Tooltip title={selectedFile.name}>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
              {selectedFile.name}
            </Typography>
          </Tooltip>
          <Tooltip title="Remove file">
            <IconButton
              size="small"
              aria-label={`Remove ${slot.label}`}
              onClick={onClear}
              sx={{ flexShrink: 0 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  );
}

function filesSatisfyConfig(
  config: WastePipelineConfig,
  files: Record<string, File | null>
): boolean {
  const allRequiredFilesSelected = config.files
    .filter((slot) => slot.required)
    .every((slot) => files[slot.key] != null);
  const atLeastOneOfRuleMet =
    !config.requireAtLeastOneOf || config.requireAtLeastOneOf.some((key) => files[key] != null);
  return allRequiredFilesSelected && atLeastOneOfRuleMet;
}

function isPairApproved(vendorInfo: VendorInfo, tenant: TenantOption): boolean {
  return vendorInfo.approvedTenants.some((approvedTenant) => approvedTenant.tenantId === tenant.id);
}

function organizationHelperText({
  vendorInfo,
  tenant,
  config,
  pipelineResolved,
}: {
  vendorInfo: VendorInfo;
  tenant: TenantOption | null;
  config: WastePipelineConfig | undefined;
  pipelineResolved: boolean;
}): { text: string | undefined; error: boolean } {
  if (tenant && !pipelineResolved) {
    return {
      text: `No ${vendorInfo.vendor} pipeline can run for ${tenant.name}.`,
      error: true,
    };
  }
  if (tenant && config?.tenantsWithCustomRules?.includes(tenant.id)) {
    return { text: `Custom ${config.label} rules apply for this tenant`, error: false };
  }
  if (!vendorInfo.hasBasePipeline) {
    return {
      text: `${vendorInfo.vendor} has no general pipeline. Only the organizations listed can run it.`,
      error: false,
    };
  }
  return { text: undefined, error: false };
}

/** The organizations the registry has a pipeline built for, for this vendor. */
function builtForTenants(vendor: string): TenantOption[] {
  return getWastePipelinesForVendor(vendor).flatMap((name) => {
    const fixedTenant = getWastePipelineConfig(name).fixedTenant;
    return fixedTenant ? [{ id: fixedTenant.id, name: fixedTenant.name }] : [];
  });
}

function resolveConfig(
  vendor: string | null,
  tenant: TenantOption | null
): WastePipelineConfig | undefined {
  if (!vendor || !tenant) return undefined;
  const pipelineName = resolveWastePipelineForVendor(vendor, tenant.id);
  return pipelineName ? getWastePipelineConfig(pipelineName) : undefined;
}

interface DispatchWastePipelineRunDialogProps {
  onClose: () => void;
}

export function DispatchWastePipelineRunDialog({ onClose }: DispatchWastePipelineRunDialogProps) {
  const alerts = useAlerts();

  const [vendor, setVendor] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null);
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [submitting, setSubmitting] = useState(false);

  const { data: vendorsData, loading: vendorsLoading } = useQuery(WASTE_PIPELINE_VENDORS_QUERY);
  const [dispatchMutation] = useMutation(DISPATCH_WASTE_PIPELINE_RUN_MUTATION, {
    // The dispatch creates the run's Pipeline row, so the runs grid behind the
    // dialog can show it as soon as the dialog closes.
    refetchQueries: [STAFF_PIPELINES_TABLE_QUERY],
  });

  const vendors = vendorsData?.wastePipelineVendors ?? [];
  const vendorInfo = vendors.find((info) => info.vendor === vendor);
  const config = resolveConfig(vendor, selectedTenant);

  const canSubmit =
    vendor != null &&
    selectedTenant != null &&
    config != null &&
    filesSatisfyConfig(config, files) &&
    !submitting;

  const handleSubmit = useCallback(async () => {
    if (!vendor || !selectedTenant || !config) return;

    setSubmitting(true);
    try {
      const sourceFiles: { key: string; s3Key: string }[] = [];

      for (const slot of config.files) {
        const file = files[slot.key];
        if (file) {
          const s3Key = await uploadFileDirectly(file, { tenantId: selectedTenant.id });
          sourceFiles.push({ key: slot.key, s3Key });
        }
      }

      const { data } = await dispatchMutation({
        variables: { input: { vendor, tenantId: selectedTenant.id, sourceFiles } },
      });

      const jobId = data?.dispatchWastePipelineRun.id;
      alerts.success(
        jobId ? (
          <>
            Pipeline dispatched successfully.{" "}
            <a href={`/staff/jobs/${jobId}`} target="_blank" rel="noopener noreferrer">
              View job
            </a>
          </>
        ) : (
          "Pipeline dispatched successfully."
        )
      );
      onClose();
    } catch (err) {
      alerts.error(
        `Failed to dispatch pipeline: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setSubmitting(false);
    }
  }, [vendor, selectedTenant, config, files, dispatchMutation, alerts, onClose]);

  const handleFileChange = (key: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (selected) {
      setFiles((prev) => ({ ...prev, [key]: selected }));
    }
    // Reset the input so the same file can be re-selected
    event.target.value = "";
  };

  const handleClearFile = (key: string) => {
    setFiles((prev) => ({ ...prev, [key]: null }));
  };

  const handleVendorChange = (value: string) => {
    setVendor(value);
    setSelectedTenant(null);
    setFiles({});
  };

  // Files belong to the resolved pipeline's slots, so they only survive a
  // tenant change when the pair still resolves to the same pipeline.
  const handleTenantChange = (value: TenantOption | null) => {
    const nextPipeline = vendor && value ? resolveWastePipelineForVendor(vendor, value.id) : null;
    const currentPipeline =
      vendor && selectedTenant ? resolveWastePipelineForVendor(vendor, selectedTenant.id) : null;
    if (nextPipeline !== currentPipeline) {
      setFiles({});
    }
    setSelectedTenant(value);
  };

  const organizationHelper = vendorInfo
    ? organizationHelperText({
        vendorInfo,
        tenant: selectedTenant,
        config,
        pipelineResolved: config != null,
      })
    : { text: undefined, error: false };
  // The pair resolves to a pipeline but has not been approved: the run is
  // allowed for QC, and the ingest gate is what blocks its output.
  const qcOnly =
    vendorInfo != null &&
    selectedTenant != null &&
    config != null &&
    !isPairApproved(vendorInfo, selectedTenant);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="New Pipeline Run"
      primaryActionButtonProps={{
        onClick: handleSubmit,
        saveText: "Dispatch",
        loading: submitting,
        disabled: !canSubmit,
      }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        <TextField
          select
          label="Vendor"
          value={vendor ?? ""}
          onChange={(e) => handleVendorChange(e.target.value)}
          disabled={vendorsLoading}
          fullWidth
        >
          {vendors.map((info) => (
            <MenuItem key={info.vendor} value={info.vendor}>
              {info.vendor}
            </MenuItem>
          ))}
        </TextField>

        {vendorInfo && (
          <DispatchOrganizationPicker
            key={vendorInfo.vendor}
            approvedTenants={vendorInfo.approvedTenants.map((tenant) => ({
              id: tenant.tenantId,
              name: tenant.tenantName,
            }))}
            builtForTenants={builtForTenants(vendorInfo.vendor)}
            hasBasePipeline={vendorInfo.hasBasePipeline}
            value={selectedTenant}
            onChange={handleTenantChange}
            helperText={organizationHelper.text}
            error={organizationHelper.error}
          />
        )}

        {qcOnly && vendorInfo && selectedTenant && (
          <Alert severity="warning">
            {vendorInfo.vendor} runs for {selectedTenant.name} can be used to check the data, but
            the output cannot be loaded into Encamp until a data manager reviews a successful run
            and approves it.
          </Alert>
        )}

        {config?.requireAtLeastOneOf && (
          <Typography variant="body2" color="text.secondary">
            Provide at least one of:{" "}
            {config.requireAtLeastOneOf
              .map((key) => config.files.find((f) => f.key === key)?.label ?? key)
              .join(" or ")}
            .
          </Typography>
        )}

        {config?.files.map((slot) => (
          <FileSlotRow
            key={slot.key}
            slot={slot}
            selectedFile={files[slot.key] ?? null}
            onSelect={(e) => handleFileChange(slot.key, e)}
            onClear={() => handleClearFile(slot.key)}
          />
        ))}
      </Stack>
    </Dialog>
  );
}
